'use client';

import type { QueryParams } from '@/lib/api/httpClient';
import { createHttpClient } from '@/lib/api/httpClient';
import {
  mapActiveOrderPageResponse,
  mapActivityPageResponse,
  mapFullActivityListResponse,
  mapLastTradeStatsResponse,
  mapOrderHistoryPageResponse,
  mapTradeHistoryPageResponse,
} from './mappers';
import type {
  ActiveOrderPageResponse,
  ActivityPageResponse,
  FullActivityListResponse,
  LastTradeStatsResponse,
  OrderHistoryPageResponse,
  TradeHistoryPageResponse,
} from './models';
import type {
  ActiveOrderPageResponseDto,
  ActiveOrderRawDto,
  ActivityPageResponseDto,
  DealsActiveOrderItem,
  DealsActiveOrdersParams,
  DealsOpenPositionItem,
  DealsOpenPositionsParams,
  DealsPageResponse,
  DealsPositionHistoryItem,
  DealsPositionHistoryParams,
  DealsRawPageResponse,
  FullActivityListResponseDto,
  LastTradeStatsItemDto,
  LastTradeStatsResponseDto,
  OpenPositionRawDto,
  OrderHistoryPageResponseDto,
  PositionHistoryRawDto,
  TradeHistoryPageResponseDto,
} from './types';
import { mapActiveOrder, mapOpenPosition, mapPositionHistory, mapRawPageResponse } from './types';

const BASE_URL = process.env.NEXT_PUBLIC_ORDER_HISTORY_URL;

/**
 * Filters out undefined/null values and empty arrays from params object
 */
function filterParams(params: Record<string, unknown>): QueryParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  ) as QueryParams;
}

// Cookie-based history client — auth rides the HttpOnly cookie
const historyHttp = createHttpClient({ baseUrl: BASE_URL });

/**
 * Builds query parameters for order history
 * eventOutcomeId is passed as explicit query parameter
 */
function buildOrderHistoryQuery(outcomeId?: string): QueryParams {
  return filterParams({ eventOutcomeId: outcomeId });
}

/**
 * Builds query parameters for active orders
 * Supports pagination, sorting, and filtering by eventOutcomeId
 */
function buildActiveOrdersQuery(options?: {
  page?: number;
  size?: number;
  sort?: string[];
  order?: string[];
  eventOutcomeId?: string;
}): QueryParams {
  return filterParams({ ...options });
}

/**
 * Builds query parameters for trades
 * According to Swagger UI, eventOutcomeId is passed as explicit query parameter
 */
function buildTradeHistoryQuery(outcomeId: string): QueryParams {
  return { eventOutcomeId: outcomeId };
}

/**
 * Fetches order history
 * GET /history/orders
 */
export async function fetchOrderHistory(): Promise<OrderHistoryPageResponse> {
  const dto = await historyHttp.get<OrderHistoryPageResponseDto>('/history/orders', {
    query: buildOrderHistoryQuery(),
  });
  return mapOrderHistoryPageResponse(dto);
}

/**
 * Fetches order history for a specific event
 * GET /history/orders/by-event/{eventId}
 */
export async function fetchOrderHistoryByEvent(
  eventId: string,
  outcomeId: string,
): Promise<OrderHistoryPageResponse> {
  const dto = await historyHttp.get<OrderHistoryPageResponseDto>(
    `/history/orders/by-event/${eventId}`,
    {
      query: buildOrderHistoryQuery(outcomeId),
    },
  );
  return mapOrderHistoryPageResponse(dto);
}

/**
 * Fetches trade history (no pagination)
 * GET /history/trades
 */
export async function fetchTradeHistory(outcomeId: string): Promise<TradeHistoryPageResponse> {
  const dto = await historyHttp.get<TradeHistoryPageResponseDto>('/history/trades', {
    query: buildTradeHistoryQuery(outcomeId),
  });
  return mapTradeHistoryPageResponse(dto);
}

/**
 * Fetches active orders for a specific event outcome
 * GET /history/orders?eventOutcomeId={outcomeId}
 * This endpoint returns active orders (status: ACTIVE) for the given outcome
 */
export async function fetchActiveOrders(options?: {
  page?: number;
  size?: number;
  sort?: string[];
  order?: string[];
  eventOutcomeId?: string;
}): Promise<ActiveOrderPageResponse> {
  const dto = await historyHttp.get<ActiveOrderPageResponseDto>('/history/orders', {
    query: buildActiveOrdersQuery(options),
  });
  return mapActiveOrderPageResponse(dto);
}

/**
 * Builds query parameters for activity by event
 */
function buildActivityQuery(options?: { page?: number; size?: number }): QueryParams {
  return filterParams({ ...options });
}

/**
 * Fetches activity history for a specific event
 * GET /history/activity/by-event/{eventId}
 */
export async function fetchActivityByEvent(
  eventId: string,
  options?: {
    page?: number;
    size?: number;
  },
): Promise<ActivityPageResponse> {
  const http = createHttpClient({ baseUrl: BASE_URL });
  const dto = await http.get<ActivityPageResponseDto>(`/history/activity/by-event/${eventId}`, {
    query: buildActivityQuery(options),
  });
  return mapActivityPageResponse(dto);
}

/**
 * Fetches full activity history across all events
 * GET /history/activity
 */
export async function fetchFullActivity(options?: {
  page?: number;
  size?: number;
}): Promise<FullActivityListResponse> {
  const dto = await historyHttp.get<FullActivityListResponseDto>('/history/activity', {
    query: buildActivityQuery(options),
  });
  return mapFullActivityListResponse(dto);
}

/**
 * Fetches last trades stats (public, no auth required)
 * GET /history/stats/last-trades
 */
export async function fetchLastTradesStats(options?: {
  page?: number;
  size?: number;
  locale?: string;
}): Promise<LastTradeStatsResponse> {
  const http = createHttpClient({ baseUrl: BASE_URL });
  const dto = await http.get<LastTradeStatsResponseDto | LastTradeStatsItemDto[]>(
    '/history/stats/last-trades',
    { query: filterParams({ periodPreset: 'ALL_TIME', ...options }) },
  );
  return mapLastTradeStatsResponse(dto);
}

// ===== Deals Controller Endpoints (/api/history/deals/*) =====

/**
 * Fetches deals open positions
 * GET /history/deals/open-positions
 */
export async function fetchDealsOpenPositions(
  params?: DealsOpenPositionsParams,
): Promise<DealsPageResponse<DealsOpenPositionItem>> {
  const raw = await historyHttp.get<DealsRawPageResponse<OpenPositionRawDto>>(
    '/history/deals/open-positions',
    { query: filterParams({ ...params }) },
  );
  return mapRawPageResponse(raw, mapOpenPosition);
}

/**
 * Fetches deals active orders
 * GET /history/deals/active-orders
 */
export async function fetchDealsActiveOrders(
  params?: DealsActiveOrdersParams,
): Promise<DealsPageResponse<DealsActiveOrderItem>> {
  const raw = await historyHttp.get<DealsRawPageResponse<ActiveOrderRawDto>>(
    '/history/deals/active-orders',
    { query: filterParams({ ...params }) },
  );
  return mapRawPageResponse(raw, mapActiveOrder);
}

/**
 * Fetches deals position history
 * GET /history/deals/position-history
 */
export async function fetchDealsPositionHistory(
  params?: DealsPositionHistoryParams,
): Promise<DealsPageResponse<DealsPositionHistoryItem>> {
  const raw = await historyHttp.get<DealsRawPageResponse<PositionHistoryRawDto>>(
    '/history/deals/position-history',
    { query: filterParams({ ...params }) },
  );
  return mapRawPageResponse(raw, mapPositionHistory);
}
